<template>
  <div class="STERiGLIDE">
    <!-- S1：Hero 區（899px 斷點切換 web / phone 圖） -->
    <section class="hero">
      <picture>
        <!-- 手機 -->
        <source media="(max-width: 899px)" :srcset="phoneHero" />
        <!-- 桌機 -->
        <img :src="webHero" alt="STERiGLIDE Hero" class="hero-img" />
      </picture>

      <!-- 文字層 -->
      <div class="hero-text">
        <h1 class="hero-title" v-html="$t('STERiGLIDE.s1.title')"></h1>
        <p class="hero-body" v-html="$t('STERiGLIDE.s1.body')"></p>
        <p class="hero-body hero-body-small mt-3" v-html="$t('STERiGLIDE.s1.body1')"></p>
      </div>
    </section>

    <!-- S2：Interstitial Bleed（滿版出血條帶，與 STERiJECT 一致） -->
    <section class="interstitial-bleed second-row">
      <div class="interstitial-inner">
        <h3 class="inter-title" v-html="$t('STERiGLIDE.s2.title')"></h3>
        <p class="inter-body" v-html="$t('STERiGLIDE.s2.body')"></p>
      </div>
    </section>

    <!-- S3：左側控制面板＋右側底圖 -->
    <section class="s3-canvas bleed-row">
      <!-- 手機專用：標題條（藍底白字，顯示在圖片上方） -->
      <div class="s3-mobile-head">
        <div class="s3-mobile-head-inner">
          <div class="ph-title" v-html="$t('STERiGLIDE.s3.title1')"></div>
          <div class="ph-title" v-html="$t('STERiGLIDE.s3.title2')"></div>
        </div>
      </div>

      <picture>
        <!-- 手機圖 -->
        <source media="(max-width: 899px)" :srcset="currentPhoneImg" />
        <!-- 桌機圖 -->
        <img :src="currentWebImg" alt="STERiGLIDE s3" class="row-img" />
      </picture>

      <!-- 左側控制面板（桌機：卡在左邊；手機：覆蓋圖片左側，單欄） -->
      <aside class="s3-panel">
        <!-- 桌機才顯示的標題條 -->
        <div class="panel-head">
          <div class="ph-title" v-html="$t('STERiGLIDE.s3.title1')"></div>
          <div class="ph-title" v-html="$t('STERiGLIDE.s3.title2')"></div>
        </div>

        <div class="btn-grid">
          <button
            v-for="btn in s3Buttons"
            :key="'s3btn-' + btn.id"
            type="button"
            :class="['s3-btn', { active: selectedS3 === btn.id }]"
            @click="selectedS3 = btn.id"
            v-html="$t(btn.label)"
          ></button>
        </div>
      </aside>

      <!-- S3 手機版：規格灰底區塊（只在 ≤899px 顯示） -->
      <div class="s3-mobile-specs" v-if="isMobile && mobileSpecs[selectedS3]">
        <div class="specs-inner">
          <!-- 左：部位（跨越右側規格的列數） -->
          <div
            class="specs-part"
            :style="{ gridRowEnd: 'span ' + mobileSpecs[selectedS3].length }"
            v-html="$t(s3Buttons.find(b => b.id === selectedS3)?.label)"
          ></div>

          <!-- 中 + 右：逐列輸出 「｜」 與 規格 -->
          <template v-for="(it, i) in mobileSpecs[selectedS3]" :key="'sp-'+selectedS3+'-'+i">
            <div class="specs-bar">｜</div>
            <div class="specs-row">
              <span class="gauge-pill" :class="gaugePillClass(it.gauge)">{{ it.gauge }}</span>
              <span class="specs-text">{{ it.text }}</span>
            </div>
          </template>
        </div>
      </div>
    </section>

    <!-- S3-2：四張圖片＋文字（新區塊） -->
    <section class="s3-2-board">
      <div class="s3-2-inner">
        <article
          v-for="(item, idx) in s32Items"
          :key="'s3-2-' + idx"
          class="s3-2-card"
        >
          <img
            :src="item.img"
            :alt="$t(item.title)"
            class="s3-2-img"
            loading="lazy"
          />
          <h4 class="s3-2-title" v-html="$t(item.title)"></h4>
          <p class="s3-2-body" v-html="$t(item.body)"></p>
        </article>
      </div>
    </section>

    <!-- S4：Left GIF + Right Text -->
    <section class="s4 bleed-row">
      <div class="s4-inner">
        <!-- 左：GIF -->
        <div class="s4-media">
          <img
            :src="require('@/assets/images/AppShell/products/STERiGLIDE/cannula.gif')"
            alt="STERiGLIDE Cannula GIF"
            class="s4-gif"
            loading="lazy"
          />
        </div>

        <!-- 右：文字 -->
        <div class="s4-copy">
          <h3 class="s4-title" v-html="$t('STERiGLIDE.s4.title')"></h3>
          <p class="s4-body1" v-html="$t('STERiGLIDE.s4.body1')"></p>
          <p class="s4-body2" v-html="$t('STERiGLIDE.s4.body2')"></p>
        </div>
      </div>
    </section>

    <!-- S5：BG image + Left Copy (desktop) / Image + Text Block (mobile) -->
    <section class="s5">
      <!-- Desktop：底圖＋左側文案 -->
      <div
        class="s5-desktop"
        :style="{ backgroundImage: `url(${require('@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_03.jpg')})` }"
      >
        <div class="s5-copy">
          <h3 class="s5-title" v-html="$t('STERiGLIDE.s5.title')"></h3>
          <p class="s5-body1" v-html="$t('STERiGLIDE.s5.body1')"></p>
          <p class="s5-body2" v-html="$t('STERiGLIDE.s5.body2')"></p>
        </div>
      </div>

      <!-- Mobile：圖片 + 下方文字區塊 -->
      <div class="s5-mobile">
        <img
          :src="require('@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_03.jpg')"
          alt="STERiGLIDE section 5"
          class="s5-img"
          loading="lazy"
        />
        <div class="s5-copybox">
          <h3 class="s5-title" v-html="$t('STERiGLIDE.s5.title')"></h3>
          <p class="s5-body1" v-html="$t('STERiGLIDE.s5.body1')"></p>
          <p class="s5-body2" v-html="$t('STERiGLIDE.s5.body2')"></p>
        </div>
      </div>
    </section>

    <!-- S6：三欄卡片（桌機橫排 / 手機直排） -->
    <section class="s6">
      <div class="s6-grid">
        <article
          v-for="(card, idx) in s6Cards"
          :key="'s6-' + idx"
          class="s6-card"
        >
          <!-- 標題：白字、方形漸層底 -->
          <h4 class="s6-title" v-html="$t(card.titleKey)"></h4>

          <!-- 內文：灰色 -->
          <p class="s6-body" v-html="$t(card.bodyKey)"></p>

          <!-- 圖片：桌機/手機切換不同來源 -->
          <picture>
            <source
              media="(max-width: 899px)"
              :srcset="card.phoneImg"
            />
            <img
              :src="card.webImg"
              :alt="`STERiGLIDE s6 image ${idx+1}`"
              class="s6-img"
              loading="lazy"
            />
          </picture>
        </article>
      </div>
    </section>

    <!-- S7：BG image + Right Copy (desktop) / Phone image + top-1/3 overlay text (mobile) -->
    <section class="s7">
      <!-- Desktop：底圖＋右側文案 -->
      <div
        class="s7-desktop"
        :style="{ backgroundImage: `url(${require('@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_05.jpg')})` }"
      >
        <div class="s7-copy">
          <h3 class="s7-title" v-html="$t('STERiGLIDE.s7.title')"></h3>
          <p class="s7-body"  v-html="$t('STERiGLIDE.s7.body')"></p>
        </div>
      </div>

      <!-- Mobile：圖片 + 上半段 1/3 覆蓋文字 -->
      <div class="s7-mobile">
        <div class="s7-mobile-wrap">
          <img
            :src="require('@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_05.jpg')"
            alt="STERiGLIDE section 7"
            class="s7-img"
            loading="lazy"
          />
          <!-- 以圖片高度的 1/3 處作為錨點 -->
          <div class="s7-mobile-text">
            <h3 class="s7-title" v-html="$t('STERiGLIDE.s7.title')"></h3>
            <p class="s7-body"  v-html="$t('STERiGLIDE.s7.body')"></p>
          </div>
        </div>
      </div>
    </section>

    <!-- 規格表（改用 STERiJECT 版型） -->
    <div class="table-bleed">
      <div class="table-inner">
        <div class="table-head">
          <span class="text-primary fw-bold"><p v-html="$t('model')"></p></span>
          <span class="thin-wall">{{ $t('STERiGLIDE.Thin-wall') }}</span>
        </div>

        <div class="table-scroller">
          <table class="table custom-table">
            <thead>
              <tr class="header-row">
                <th scope="col">Gauge</th>
                <th scope="col">Code</th>
                <th scope="col">Description</th>
                <th scope="col">Inch</th>
                <th scope="col">*TSKiD™</th>
                <th scope="col">Box</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(group, groupIndex) in groupedData" :key="groupIndex">
                <tr
                  v-for="(item, itemIndex) in group.items"
                  :key="item.code"
                  :class="getRowClass(displayIndex(groupIndex, itemIndex))"
                >
                  <td v-if="itemIndex === 0" :rowspan="group.items.length" class="gauge-cell">
                    {{ group.gauge }}
                  </td>
                  <td>{{ item.code }}</td>
                  <td>{{ item.description }}</td>
                  <td>{{ item.inch }}</td>
                  <td>{{ item.utw }}</td>
                  <td>{{ item.box }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'STERiGLIDEPage',
  data () {
    return {
      // 圖片路徑
      webHero: require('@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_01.jpg'),
      phoneHero: require('@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_01.jpg'),

      // ★ S3-2：四張圖片＋文字
      s32Items: [
        {
          img: require('@/assets/images/AppShell/products/STERiGLIDE/STERiGLIDE_00_01.png'),
          title: 'STERiGLIDE.s3-2.title1',
          body:  'STERiGLIDE.s3-2.body1'
        },
        {
          img: require('@/assets/images/AppShell/products/STERiGLIDE/STERiGLIDE_00_02.png'),
          title: 'STERiGLIDE.s3-2.title2',
          body:  'STERiGLIDE.s3-2.body2'
        },
        {
          img: require('@/assets/images/AppShell/products/STERiGLIDE/STERiGLIDE_00_03.png'),
          title: 'STERiGLIDE.s3-2.title3',
          body:  'STERiGLIDE.s3-2.body3'
        },
        {
          img: require('@/assets/images/AppShell/products/STERiGLIDE/STERiGLIDE_00_04.png'),
          title: 'STERiGLIDE.s3-2.title4',
          body:  'STERiGLIDE.s3-2.body4'
        }
      ],

      // 第三段：預設選第 1 顆
      selectedS3: 1,
      s3Buttons: [
        { id: 1,  label: 'STERiGLIDE.s3.k1',  img: null     }, // 預設底圖
        { id: 2,  label: 'STERiGLIDE.s3.k2',  img: '01'     },
        { id: 3,  label: 'STERiGLIDE.s3.k3',  img: '02'     },
        { id: 4,  label: 'STERiGLIDE.s3.k4',  img: '03'     },
        { id: 5,  label: 'STERiGLIDE.s3.k5',  img: '04'     },
        { id: 6,  label: 'STERiGLIDE.s3.k6',  img: '05'     },
        { id: 7,  label: 'STERiGLIDE.s3.k7',  img: '06'     },
        { id: 8,  label: 'STERiGLIDE.s3.k8',  img: '07'     },
        { id: 9,  label: 'STERiGLIDE.s3.k9',  img: '08'     },
        { id: 10, label: 'STERiGLIDE.s3.k10', img: '09'     },
        { id: 11, label: 'STERiGLIDE.s3.k11', img: '10'     },
        { id: 12, label: 'STERiGLIDE.s3.k12', img: '11'     },
        { id: 13, label: 'STERiGLIDE.s3.k13', img: '12'     },
        { id: 14, label: 'STERiGLIDE.s3.k14', img: '13'     }
      ],
      mobileSpecs: {
        // 依你的清單，鍵值用「按鈕 id」
        2:  [ { gauge:'25G', text:'38mm/50mm' } ],                                          // 鼻
        3:  [ { gauge:'22G', text:'50mm/70mm' }, { gauge:'25G', text:'38mm/50mm' } ],    // 前額
        4:  [ { gauge:'25G', text:'38mm/50mm' } ],                                          // 法令紋
        5:  [ { gauge:'25G', text:'38mm/50mm' }, { gauge:'30G', text:'25mm' } ],           // 眉間紋
        6:  [ { gauge:'22G', text:'50mm/70mm' }, { gauge:'25G', text:'50mm' } ],           // 下顎/上顎線
        7:  [ { gauge:'22G', text:'38mm/50mm' }, { gauge:'25G', text:'38mm/50mm' } ],    // 淺層太陽穴
        8:  [ { gauge:'25G', text:'38mm/50 mm' } ],                                         // 下頜/下顎線（原文有空格我原樣保留）
        9:  [ { gauge:'22G', text:'50mm/70mm' }, { gauge:'25G', text:'50mm' } ],           // 耳屏前/耳前
        10: [ { gauge:'25G', text:'38mm/50mm' }, { gauge:'27G', text:'38mm/50mm' } ],    // 唇角/唇
        11: [ { gauge:'25G', text:'38mm' }, { gauge:'27G', text:'25mm/38mm/50mm' }, { gauge:'30G', text:'25mm' } ], // 淚溝/黑眼圈
        12: [ { gauge:'25G', text:'38mm/50mm' } ],                                          // 唇部/下巴
        13: [ { gauge:'22G', text:'38mm/50mm/70mm' }, { gauge:'25G', text:'38mm/50mm' } ], // 顴骨/中面部
        14: [ { gauge:'25G', text:'38mm/50mm' } ]                                           // 頸
      },
      s3PreloadPool: [],
      isMobile: false,
      mq: null,
      // S6：三卡片（title/body 走 i18n，圖用 web/phone 兩套）
      s6Cards: [
        {
          titleKey: 'STERiGLIDE.s6.title1',
          bodyKey:  'STERiGLIDE.s6.body1',
          webImg:   require('@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_04_01.jpg'),
          phoneImg: require('@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_04_01.jpg')
        },
        {
          titleKey: 'STERiGLIDE.s6.title2',
          bodyKey:  'STERiGLIDE.s6.body2',
          webImg:   require('@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_04_02.jpg'),
          phoneImg: require('@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_04_02.jpg')
        },
        {
          titleKey: 'STERiGLIDE.s6.title3',
          bodyKey:  'STERiGLIDE.s6.body3',
          webImg:   require('@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_04_03.jpg'),
          phoneImg: require('@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_04_03.jpg')
        }
      ],

      // 規格表資料
      tableData: [
        { gauge: '30G', code: 'SGC-30025', description: 'STERiGLIDE Cannula 30G x 25mm', inch: '1”',  utw: '√', box: '20' },
        { gauge: '27G', code: 'SGC-27025', description: 'STERiGLIDE Cannula 27G x 25mm', inch: '1”',  utw: '√', box: '20' },
        { gauge: '27G', code: 'SGC-27038', description: 'STERiGLIDE Cannula 27G x 38mm', inch: '1½”', utw: '√', box: '20' },
        { gauge: '27G', code: 'SGC-27050', description: 'STERiGLIDE Cannula 27G x 50mm', inch: '2”',  utw: '√', box: '20' },
        { gauge: '25G', code: 'SGC-25038', description: 'STERiGLIDE Cannula 25G x 38mm', inch: '1½”', utw: '√', box: '20' },
        { gauge: '25G', code: 'SGC-25050', description: 'STERiGLIDE Cannula 25G x 50mm', inch: '2”',  utw: '√', box: '20' },
        { gauge: '22G', code: 'SGC-22038', description: 'STERiGLIDE Cannula 22G x 38mm', inch: '1½”', utw: '√', box: '20' },
        { gauge: '22G', code: 'SGC-22050', description: 'STERiGLIDE Cannula 22G x 50mm', inch: '2”',  utw: '√', box: '20' },
        { gauge: '22G', code: 'SGC-22070', description: 'STERiGLIDE Cannula 22G x 70mm', inch: '2¾”', utw: '√', box: '20' }
      ]
    }
  },
  mounted () {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mq = window.matchMedia('(max-width: 899px)')
      // 初始判斷
      this.isMobile = this.mq.matches
      if (this.isMobile && this.selectedS3 === 1) {
        this.selectedS3 = 2
      }
      // 監聽斷點變化
      const handler = (e) => {
        this.isMobile = e.matches
        // 只有「剛進手機版」且還在 1 的時候，才自動切到 2
        if (e.matches && this.selectedS3 === 1) {
          this.selectedS3 = 2
        }
        // 回到桌機版不強制改回，避免干擾使用者已選的項目
      }
      // 新舊 API 兼容
      if (this.mq.addEventListener) this.mq.addEventListener('change', handler)
      else this.mq.addListener(handler)
      this.mqHandler = handler
    }
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mq = window.matchMedia('(max-width: 899px)')
      this.isMobile = this.mq.matches
      if (this.isMobile && this.selectedS3 === 1) this.selectedS3 = 2
      const handler = (e) => {
        this.isMobile = e.matches
        if (e.matches && this.selectedS3 === 1) this.selectedS3 = 2
      }
      if (this.mq.addEventListener) this.mq.addEventListener('change', handler)
      else this.mq.addListener(handler)
      this.mqHandler = handler
    }

    // ⭐ 關鍵：頁面進來就預載所有 S3 圖片（web / phone）
    this.preloadS3Images()
  },
  beforeUnmount () {
    if (this.mq) {
      if (this.mq.removeEventListener && this.mqHandler) {
        this.mq.removeEventListener('change', this.mqHandler)
      } else if (this.mq.removeListener && this.mqHandler) {
        this.mq.removeListener(this.mqHandler)
      }
    }
  },
  computed: {
    // 目前要顯示的桌機圖
    currentWebImg () {
      const btn = this.s3Buttons.find(b => b.id === this.selectedS3)
      return this._s3WebUrl(btn)
    },
    currentPhoneImg () {
      const btn = this.s3Buttons.find(b => b.id === this.selectedS3)
      return this._s3PhoneUrl(btn)
    },
    groupedData () {
      const groups = this.tableData.reduce((acc, item) => {
        const g = acc.find(x => x.gauge === item.gauge)
        if (g) g.items.push(item)
        else acc.push({ gauge: item.gauge, items: [item] })
        return acc
      }, [])
      return groups
    }
  },
  methods: {
    _s3WebUrl (btn) {
      if (!btn || !btn.img) {
        return require('@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_02.jpg')
      }
      return require(`@/assets/images/AppShell/products/STERiGLIDE/web/STERiGLIDE_02_${btn.img}.jpg`)
    },
    _s3PhoneUrl (btn) {
      if (!btn || !btn.img) {
        return require('@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_02.jpg')
      }
      return require(`@/assets/images/AppShell/products/STERiGLIDE/phone/STERiGLIDE_02_${btn.img}.jpg`)
    },

    // ---- 真正的預載器 ----
    preloadS3Images () {
      // 若使用者省流量模式，就別硬預載整包
      const saveData = navigator.connection && navigator.connection.saveData
      if (saveData) return

      // 組出所有要預載的 URL（包含 id=1 的預設底圖）
      const urls = []
      for (const btn of this.s3Buttons) {
        urls.push(this._s3WebUrl(btn))
        urls.push(this._s3PhoneUrl(btn))
      }

      // 去除重複，避免白費力氣
      const unique = Array.from(new Set(urls))

      // 逐一丟進快取
      unique.forEach((u) => {
        const img = new Image()
        // 如要看 log 可打開：
        // img.onload = () => console.debug('[S3 preload] OK', u)
        // img.onerror = () => console.warn('[S3 preload] FAIL', u)
        img.src = u
        this.s3PreloadPool.push(img)
      })

      // （選配）對關鍵圖也加個 <link rel="preload">，對部分瀏覽器更積極
      // 注意：preload 太多可能噴警告，所以只挑前幾張
      const head = document.head
      unique.slice(0, 6).forEach((u) => {
        const link = document.createElement('link')
        link.rel = 'preload'
        link.as = 'image'
        link.href = u
        head.appendChild(link)
      })
    },
    displayIndex (groupIndex, itemIndex) {
      let index = 0
      for (let i = 0; i < groupIndex; i++) index += this.groupedData[i].items.length
      return index + itemIndex
    },
    getRowClass (index) {
      return index % 2 === 0 ? 'odd-row' : 'even-row'
    },
    gaugePillClass (g) {
      // 回傳對應的 class 讓 CSS 上色
      const key = (g || '').replace(/\s+/g, '').toUpperCase(); // '22G','25G',...
      return {
        'g22': key === '22G',
        'g25': key === '25G',
        'g27': key === '27G',
        'g30': key === '30G'
      }
    }
  }
}
</script>

<style scoped>
.STERiGLIDE .text-primary,
.STERiGLIDE .inter-title,
.STERiGLIDE .s3-2-title,
.STERiGLIDE .s4-title,
.STERiGLIDE .s5-title,
.STERiGLIDE .s7-title {
  color: #0071bb !important;
}

/* —— S1：Hero —— */
.hero {
  position: relative;
  width: 100%;
  line-height: 0; /* 移除 img 下方空隙 */
}
.hero-img {
  width: 100%;
  height: auto;
  display: block;
}

/* 文字：預設桌機在右半邊，白字、無陰影 */
.hero-text {
  position: absolute;
  inset: 0 auto 0 50%;
  padding: 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #fff !important;
  text-align: left;
  max-width: 650px;
  text-shadow: none;          /* 不要陰影 */
}

/* —— 流體字體（桌機）——
   clamp(最小字級, 理想字級(含vw), 最大字級)
   這樣會在寬螢幕時更大、窄些時更小，但不會小到難讀或大到破版
*/
.hero-title {
  font-weight: 700;
  line-height: 1.15;
  margin: 0 0 12px;
  font-size: clamp(28px, 3.2vw, 56px);
  letter-spacing: 0.02em;
  color: #fff !important;
}
.hero-title sup {
  font-size: 0.3em;
  vertical-align: super;
  line-height: 1;
}


.hero-body {
  line-height: 1.7;
  margin: 0;
  /* 內文也做流體字級，但範圍比標題小 */
  font-size: clamp(15px, 1.3vw, 20px);
}

/* body1 專用的小字 */
.hero-body-small {
  font-size: clamp(13px, 1vw, 16px);  /* 比一般 hero-body 再小一階 */
  opacity: 0.9;                       /* 視覺上也能更柔和 */
}

/* —— 手機（≤ 899px）：上半段置中，字級範圍調整 —— */
@media (max-width: 899px) {
  .hero-text {
    inset: 10% 0 auto 0;        /* 放在上半段 */
    padding: 20px 16px 0;
    max-width: none;
    text-align: center;
    align-items: center;
  }
  .hero-title {
    /* 手機上讓標題更有彈性一些 */
    font-size: clamp(22px, 7vw, 34px);
  }
  .hero-body {
    font-size: clamp(14px, 4vw, 18px);
  }

  .hero-body-small {
    font-size: clamp(14px, 1vw, 18px);
  }
}

/* —— S2：Interstitial Bleed —— */
.interstitial-bleed {
  --content-pad: 24px;
  width: calc(100% + (var(--content-pad) * 2));
  margin-left: calc(var(--content-pad) * -1);
  margin-right: calc(var(--content-pad) * -1);
  background: #f6f6f6;

  /* 單純置中：水平 + 垂直 */
  display: flex;
  align-items: center;
  justify-content: center;

  /* 用最小高度確保不會視覺太扁 */
  min-height: 360px;        /* 可依設計調整 */
  padding: 0 20px;          /* 左右留白 */
}

@media (max-width: 899px) {
  .interstitial-bleed {
    --content-pad: 16px;
    min-height: 420px;      /* 手機給高一點更穩 */
    padding: 0 16px;
  }
}

.interstitial-inner {
  width: 60%;
  max-width: 1100px;
  text-align: center;
  line-height: 1.6;
}

@media (max-width: 899px) {
  .interstitial-inner { width: 100%; }
}

/* 明確定義標題/內文樣式，避免被全域覆蓋 */
.inter-title {
  margin: 0 0 10px;
  font-weight: 800;
  color: #306fb4;
  font-size: clamp(24px, 2.6vw, 44px);
}

.second-row .interstitial-inner { container-type: inline-size; }

@supports (font-size: 1cqw) {
  .second-row .inter-body {
    font-size: clamp(14px, 2.4cqw, 20px);
    line-height: 1.7;
    max-width: 70ch;
    margin-inline: auto;
    color: #000;
  }
}
@supports not (font-size: 1cqw) {
  .second-row .inter-body {
    font-size: clamp(14px, 1.6vw, 20px);
    line-height: 1.7;
    max-width: 70ch;
    margin-inline: auto;
    color: #000;
  }
}

/* —— 共用：滿版出血容器 —— */
.bleed-row {
  --content-pad: 24px;
  position: relative;
  width: calc(100% + (var(--content-pad) * 2));
  margin-left: calc(var(--content-pad) * -1);
  margin-right: calc(var(--content-pad) * -1);
  line-height: 0;
}
@media (max-width: 899px) { .bleed-row { --content-pad: 16px; } }
.row-img { display:block; width:100%; height:auto; }

/* —— S3：左控右圖 —— */
.s3-canvas { container-type: inline-size; position: relative; }

/* 左側控制面板（桌機版） */
.s3-panel {
  position: absolute;
  top: 50%;
  left: clamp(24px, 6vw, 80px);
  transform: translateY(-50%);
  width: min(420px, 34cqw);
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #f6f6f6;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  z-index: 2;
}

/* 桌機的藍底標題條（手機會隱藏，改用上方的 s3-mobile-head） */
.panel-head {
  background: #2568ad;
  color: #fff;
  padding: 14px 18px;
  border-radius: 6px;
}
.ph-title { font-weight: 800; line-height: 1.2; font-size: clamp(16px, 1.8vw, 20px); }

/* 兩欄按鈕網格（桌機） */
.btn-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

/* 按鈕：預設白底邊框；選中用藍色漸層 */
.s3-btn {
  appearance: none;
  border: 1px solid #d9d9d9;
  background: #fff;
  padding: 10px 12px;
  border-radius: 8px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  cursor: pointer;
  font-size: clamp(13px, 1.4vw, 16px);
  transition: all .2s ease;
}
.s3-btn:hover { border-color: #2568ad; box-shadow: 0 0 0 2px rgba(37,104,173,.08) inset; }
.s3-btn.active {
  color: #fff;
  border-color: transparent;
  background: linear-gradient(90deg, #2568ad, #00b5ff);
}

/* —— 手機版：標題條在圖片上方；按鈕蓋在圖片左側且單欄 —— */

/* 手機上方藍底標題條（只有手機顯示） */
.s3-mobile-head {
  display: none;
}
@media (max-width: 899px) {
  .s3-mobile-head {
    display: block;
    width: 100%;
    background: #2568ad;
    color: #fff;
  }
  .s3-mobile-head-inner {
    padding: 14px 16px;
    width: 100%;
    box-sizing: border-box;
  }
  .s3-mobile-head .ph-title {
    font-weight: 800;
    line-height: 1.25;
    font-size: clamp(16px, 4.6vw, 20px);
  }

  /* 手機把面板改為覆蓋在圖片左側，直向單欄 */
  .s3-panel {
    position: absolute;
    top: 50%;
    left: 8px;
    transform: translateY(-45%);
    width: clamp(70px, 42vw, 140px);
    padding: 12px;
    border-radius: 10px;
    background: rgba(246,246,246,.98);
    backdrop-filter: saturate(120%) blur(2px);
  }

  /* 手機隱藏面板內的標題（用上面的 s3-mobile-head 取代） */
  .s3-panel .panel-head { display: none; }

  /* 手機按鈕單欄 */
  .btn-grid { grid-template-columns: 1fr; gap: 12px; }

  /* 手機上字體略大一點、觸控更好按 */
  .s3-btn { font-size: clamp(14px, 3.8vw, 16px); padding: 4px; }
  /* 手機：隱藏第一顆（預設圖）按鈕 */
  .btn-grid .s3-btn:first-child {
    display: none;
  }
  /* —— S3 手機版：規格灰底區塊 —— */
  .s3-mobile-specs { display: none; }

  @media (max-width: 899px) {
    .s3-mobile-specs {
      display: block;
      background: #f0f0f0;
      padding: 16px 0 18px;
    }

    .s3-mobile-specs .specs-inner {
      /* 吃滿整個螢幕寬，讓中欄=畫面正中 */
      width: 100%;
      margin: 0 auto;
      padding: 0 16px;          /* 兩側安全內距 */
      box-sizing: border-box;

      /* 1fr | 14px | 1fr => 中欄固定寬，永遠在 50% */
      display: grid;
      grid-template-columns: 1fr 14px 1fr;
      column-gap: 14px;
      row-gap: 10px;
      align-items: center;
    }

    /* 左欄：部位（跨越右側列數），往中欄貼齊 */
    .specs-part {
      font-weight: 800;
      font-size: clamp(16px, 5vw, 20px);
      color: #000;
      text-align: right;
      padding-right: 8px;
      line-height: 1.3;
      justify-self: end;
    }

    /* 中欄：「｜」固定寬，置中顯示（就在 50% 的那條） */
    .specs-bar {
      font-weight: 800;
      font-size: clamp(18px, 6vw, 22px);
      line-height: 1;
      text-align: center;
      width: 14px;
    }

    /* 右欄：每列規格，從中欄右側開始 */
    .specs-row {
      display: flex;
      align-items: center;
      gap: 10px;
      justify-self: start;
    }

    /* 彩色膠囊 */
    .gauge-pill{
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 56px;
      height: 28px;
      padding: 0 10px;
      border-radius: 999px;
      font-weight: 800;
      font-size: 14px;
      color: #fff;
    }
    .gauge-pill.g22{ background: #595757; }
    .gauge-pill.g25{ background: #df6623; }
    .gauge-pill.g27{ background: #b5b5b6; }
    .gauge-pill.g30{ background: #8bbadf; }

    .specs-text{
      font-size: 16px;
      font-weight: 800;
      color: #000;
    }
  }
/* 修正 S3 手機規格區因繼承 .bleed-row 的 line-height:0 造成疊字 */
@media (max-width: 899px) {
  .s3-mobile-specs,
  .s3-mobile-specs .specs-inner,
  .s3-mobile-specs .specs-row,
  .s3-mobile-specs .specs-text {
    line-height: 1.0;  /* 或 normal，也可以 1.5~1.6 看字體舒服度 */
  }
}

}

/* —— S3-2：四張圖片＋文字（桌機橫排 / 手機直排） —— */
.s3-2-board {
  --content-pad: 24px;
  width: calc(100% + (var(--content-pad) * 2));
  margin-left: calc(var(--content-pad) * -1);
  margin-right: calc(var(--content-pad) * -1);
  background: #fff;
  padding-block: 40px;           /* 視覺高度感跟 S3/S4 接起來 */
}

@media (max-width: 899px) {
  .s3-2-board {
    --content-pad: 16px;
    padding-block: 24px;
  }
}

.s3-2-inner {
  width: 60%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  gap: 24px;
  justify-content: space-between;
}

@media (max-width: 899px) {
  .s3-2-inner {
    width: 100%;
    padding: 0 16px;
    flex-direction: column;      /* 手機改成直排 */
    align-items: center;
  }
}

.s3-2-card {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.s3-2-img {
  width: 100%;
  max-width: 260px;              /* 四張並排會比較瘦一點 */
  height: auto;
  display: block;
}

/* 標題：藍色 */
.s3-2-title {
  margin: 12px 0 6px;
  font-weight: 700;
  color: #306fb4;
}

/* 內文：灰色 */
.s3-2-body {
  margin: 0;
  color: #666;
  line-height: 1.6;
  font-size: 0.95rem;
  max-width: 38ch;
}

/* —— S4：Left GIF + Right Text —— */
.s4 {
  background: #fff;
  /* 統一高度比例：1920x800 */
  aspect-ratio: 1920 / 800;
  display: grid;
  place-items: center;
}

.s4-inner {
  /* 兩欄：左圖右文 */
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: clamp(16px, 3vw, 48px);
  width: 100%;
  height: 100%;
  padding: clamp(16px, 3vw, 40px);
  box-sizing: border-box;
}

/* 左邊 GIF 置中且不變形 */
.s4-media {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
}
.s4-gif {
  width: 100%;
  height: 100%;
  object-fit: contain; /* 保持比例、完整顯示 GIF */
}

/* 右邊文案：字級沿用 S2 的規則 */
.s4-copy { text-align: left; line-height: 1.6; }

.s4-title {
  /* 參考 .inter-title */
  margin: 0 0 12px;
  font-weight: 800;
  color: #306fb4;          /* 藍色標題 */
  font-size: clamp(20px, 2.6vw, 30px);
}

.s4-body1 {
  /* 參考 .inter-body（黑色） */
  font-size: clamp(12px, 1.6vw, 18px);
  line-height: 1.7;
  color: #000;
  margin: 0 0 10px;
  max-width: 68ch;
  text-wrap: balance;
}

.s4-body2 {
  /* 灰色補充（比 body1 稍淡、同字級或略小） */
  font-size: clamp(12px, 1.5vw, 18px);
  line-height: 1.7;
  color: #666;
  margin: 0;
  max-width: 68ch;
  text-wrap: balance;
}

/* 手機（≤899px）：上下堆疊、給較高內容空間 */
@media (max-width: 899px) {
  .s4 {
    aspect-ratio: auto;           /* 放掉固定比例，讓內容自然撐高 */
    padding: 0;                    /* 內距交給 s4-inner 管 */
  }
  .s4-inner {
    grid-template-columns: 1fr;    /* 單欄 */
    gap: 16px;
    padding: 16px;
  }
  .s4-media { height: auto; }
  .s4-gif { max-height: 40vh; }    /* 避免 GIF 過高 */
  .s4-copy { padding: 20px; }
}

/* —— S5：BG image + Left Copy —— */
.s5 { width: 100%; }

.s5-desktop {
  display: grid;
  place-items: center;
  aspect-ratio: 1920 / 800;                 /* 需求比例 */
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

/* 左側文案框：靠左置中 */
.s5-copy {
  position: absolute;
  inset: 0 auto 0 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;

  /* 與 S3/S4 一致的左右安全邊距 */
  padding-left: clamp(24px, 6vw, 80px);
  padding-right: clamp(16px, 3vw, 40px);

  max-width: min(680px, 48%);
  text-align: left;
  line-height: 1.6;
}

/* 字級參考 S4（而 S4 又是依 S2 調整過） */
.s5-title {
  margin: 0 0 8px;
  font-weight: 800;
  color: #306fb4;                  /* 藍色標題 */
  font-size: clamp(20px, 2.6vw, 30px);
}
.s5-body1 {
  font-size: clamp(12px, 1.6vw, 18px);
  line-height: 1.7;
  color: #000;                     /* 黑色主文 */
  margin: 0 0 8px;
  max-width: 68ch;
  text-wrap: balance;
}
.s5-body2 {
  font-size: clamp(12px, 1.5vw, 18px);
  line-height: 1.7;
  color: #666;                     /* 灰色補充 */
  margin: 0;
  max-width: 68ch;
  text-wrap: balance;
}

/* —— 手機（≤899px）：圖片在上 + 文字區塊在下 —— */
.s5-mobile { display: none; }
@media (max-width: 899px) {
  .s5-desktop { display: none; }
  .s5-mobile {
    display: block;
    width: 100%;
  }
  .s5-img {
    display: block;
    width: 100%;
    height: auto;
  }
  .s5-copybox {
    padding: 16px;
    background: #ffffff;           /* 與圖片分層，閱讀性更好 */
    line-height: 1.6;
    text-align: left;            /* 手機置中視覺更穩 */
    padding: 20px;
  }
}

/* —— S6：三欄卡片 —— */
.s6 {
  width: 100%;
  background: #fff;
  padding: clamp(16px, 3vw, 40px) 0;
}

.s6-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(12px, 2vw, 24px);
  align-items: start;
}

.s6-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* 標題：白字、方形漸層底（#2568ad → #00b5ff） */
.s6-title {
  margin: 0;
  color: #fff;
  font-weight: 800;
  line-height: 1.25;
  font-size: clamp(16px, 2vw, 22px);
  background: linear-gradient(90deg, #2568ad, #00b5ff);
  padding: 12px 14px;
  border-radius: 6px;     /* 正方塊感，如果你想更「方」，可改成 4px 或 0 */
  display: inline-block;  /* 讓背景看起來像一塊色塊 */
}

/* 內文：灰色 */
.s6-body {
  margin: 0;
  color: #666;
  line-height: 1.7;
  font-size: clamp(14px, 1.3vw, 16px);
}

/* 圖片：鋪滿寬度 */
.s6-img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 6px;
}

/* 手機斷點：改為直排 */
@media (max-width: 899px) {
  .s6-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .s6-title {
    font-size: clamp(16px, 5vw, 20px);
  }
  .s6-body {
    font-size: clamp(14px, 4vw, 16px);
  }
}

/* —— S7：BG image + Right Copy —— */
.s7 { width: 100%; }

.s7-desktop {
  display: grid;
  place-items: center;
  aspect-ratio: 1920 / 800;                 /* 與 S5 一致的視覺比例 */
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

/* 右側文案框：靠右置中（鏡像 S5 的左側文案） */
.s7-copy {
  position: absolute;
  inset: 0 0 0 auto;             /* 鎖在右側 */
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;

  /* 與 S3/S5 一致的左右安全邊距 */
  padding-right: clamp(24px, 6vw, 80px);
  padding-left:  clamp(16px, 3vw, 40px);

  max-width: min(680px, 48%);
  text-align: left;
  line-height: 1.6;
}

/* 標題與內文樣式延續 S4/S5（藍標題 / 灰內文） */
.s7-title {
  margin: 0 0 8px;
  font-weight: 800;
  color: #306fb4;                          /* 藍色標題 */
  font-size: clamp(20px, 2.6vw, 30px);
}
.s7-body {
  font-size: clamp(12px, 1.6vw, 18px);
  line-height: 1.7;
  color: #666;                             /* 灰色內文 */
  margin: 0;
  max-width: 68ch;
  text-wrap: balance;
}

/* —— 手機（≤899px）：圖片 + 上 1/3 覆蓋文字 —— */
.s7-mobile { display: none; }
@media (max-width: 899px) {
  .s7-desktop { display: none; }
  .s7-mobile  { display: block; width: 100%; }

  .s7-mobile-wrap {
    position: relative;
    width: 100%;
  }
  .s7-img {
    display: block;
    width: 100%;
    height: auto;
  }

  /* 覆蓋文字：放在圖片高度約 1/3 處，附淡白底提高可讀性 */
  .s7-mobile-text {
    position: absolute;
    left: 0;
    top: 30%;                     /* 大約在圖片上 1/3 處 */
    transform: translateY(-50%);
    padding: 0 16px;              /* 兩側留白避免文字貼邊 */
    width: 100%;
    text-align: left;             /* 靠左對齊 */
    background: none;             /* 移除白色背景 */
    box-shadow: none;             /* 移除陰影 */
  }

  .s7-mobile .s7-title {
    font-size: clamp(18px, 5vw, 22px);
    color: #306fb4;               /* 藍色標題 */
  }
  .s7-mobile .s7-body {
    font-size: clamp(14px, 4vw, 16px);
    color: #666;                  /* 灰色文字 */
  }
}

/* —— 規格表（灰底貼齊 AppShell 內容邊緣） —— */
.table-bleed {
  --content-pad: 24px;
  background: #f0f0f0;
  margin-top: 40px;
  padding: 40px 0;
  width: calc(100% + (var(--content-pad) * 2));
  margin-left: calc(var(--content-pad) * -1);
  margin-right: calc(var(--content-pad) * -1);
}
@media (max-width: 899px) {
  .table-bleed { --content-pad: 16px; padding: 24px 0; margin-top: 24px; }
}
.table-inner { width: 60%; max-width: 1100px; margin: 0 auto; }
@media (max-width: 899px) { .table-inner { width: 100%; padding: 0 16px; } }

.table-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}
.table-head .thin-wall { font-style: italic; color: #000; }

.table-scroller { overflow-x: auto; }

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 5px;
}
.table th, .table td { padding: 0.75rem; vertical-align: middle; }
.table thead th {
  background-color: #50A7DF;
  color: #fff;
  border-bottom: 2px solid #dee2e6;
}
.table tbody tr { border-top: 5px solid #fff; }
.table tbody td {
  border-top: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
}
.table tbody td:last-child { border-right: none; }

.custom-table .gauge-cell {
  background-color: #50A7DF;
  color: #fff;
  text-align: center;
}
.custom-table .odd-row  { background: linear-gradient(to right, #fff 0%, #d9edff 100%); }
.custom-table .even-row { background: linear-gradient(to left,  #fff 0%, #d9edff 100%); }

</style>
